import _ from 'lodash';
import classNames from 'classnames';
import inflection from 'inflection';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import pinIcon from '../icons/prefilled/16x16/pinOutlineWithStroke.svg';
import { usePostSeries } from '../series/useAllSeries';
import postPath from '../utils/url/postPath';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import PostDetailPreview from './PostDetailPreview';
import PostIcon from './PostIcon';
import styles from './PostRow.module.scss';
import PostTitleWithIcons from './PostTitleWithIcons';
import PostSeriesMeta from './series/PostSeriesMeta';
import HoverPreviewable from '../components/HoverPreviewable';
import { useAllUsersMap } from '../user/useAllUsers';
import NestableLink from '../utils/NestableLink';
import AvatarStack from '../components/AvatarStack';
import OverflowMenu from '../components/OverflowMenu';
import Button from '../components/Button';

const POST_ROW_POST_FRAGMENT = gql(`
  fragment PostRow_post on SlimPostFields {
    id
    publishedAt
    userViewedAt
    updatedAt
    views
    type
    ... on Post {
      insertedAt
    }
    ...usePostSeries_post
    contributors {
      id
    }
    ...PostIcon_post
    ...postPath_post
    ...PostTitleWithIcons_post
  }
`);

type PostFragment = DocumentType<typeof POST_ROW_POST_FRAGMENT>;

export type MetadataAttribute =
  | 'popularity'
  | 'publishedAt'
  | 'updatedAt'
  | 'userViewedAt'
  | 'insertedAt';

const getMetadata = (
  post: PostFragment,
  attribute: MetadataAttribute | null,
) => {
  switch (attribute) {
    case 'popularity':
      return `${post.views} ${inflection.inflect('view', post.views)}`;
    case 'publishedAt':
      return post.publishedAt && <Timestamp date={post.publishedAt} />;
    case 'userViewedAt':
      return post.userViewedAt && <Timestamp date={post.userViewedAt} />;
    case 'insertedAt':
      return (
        'insertedAt' in post &&
        post.insertedAt && <Timestamp date={post.insertedAt} />
      );
    default:
      return <Timestamp date={post.updatedAt} />;
  }
};

interface PostRowProps {
  pinned?: boolean;
  post: PostFragment;
  seriesHoverCardEnabled?: boolean;
  metadataAttribute?: MetadataAttribute | null;
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
  forcePreviewOpen?: boolean;
}

const PostRow = ({
  renderOverflowMenu,
  pinned = false,
  post,
  seriesHoverCardEnabled = false,
  metadataAttribute = null,
  forcePreviewOpen,
}: PostRowProps) => {
  const path = postPath(post);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const postSeries = usePostSeries(post);

  const meta = getMetadata(post, metadataAttribute);

  const allUsers = useAllUsersMap();
  const users = _.compact(
    post.contributors
      .map((contributor) => contributor.id)
      .map((id) => allUsers[id]),
  ).slice(0, 3);

  return (
    <NestableLink
      to={path}
      className={styles.link}
      aria-label={`Post: ${post.title}`}
      role="article"
    >
      <HoverPreviewable
        renderPreview={({ isOpen }) => (
          <PostDetailPreview postId={post.id} open={isOpen} />
        )}
        forcePreviewOpen={forcePreviewOpen}
      >
        {(targetProps) => (
          <div className={styles.container}>
            <div className={styles.iconContainer}>
              {pinned ? (
                <>
                  <PostIcon post={post} />
                  <div
                    className={classNames(styles.pinContainer, {
                      [styles.post]: post.type === PostType.Post,
                      [styles.template]: post.type === PostType.Template,
                    })}
                  >
                    <SVGIcon size="small">{pinIcon}</SVGIcon>
                  </div>
                </>
              ) : (
                <PostIcon post={post} />
              )}
              {/* TODO: Fix this the next time the file is edited. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <Link
                to={path}
                className={styles.hoverTarget}
                aria-hidden
                tabIndex={-1}
                {...targetProps}
              />
            </div>
            <div className={styles.titleContainer}>
              {!!postSeries && (
                <header className={styles.aboveTitle}>
                  <Link className={styles.seriesHoverTargetContainer} to={path}>
                    <div className={styles.seriesHoverTarget}>
                      <PostSeriesMeta
                        postId={post.id}
                        series={postSeries}
                        showHoverCard={seriesHoverCardEnabled}
                        longHoverDelay={seriesHoverCardEnabled}
                      />
                    </div>
                  </Link>
                </header>
              )}
              <span className={styles.title} role="heading" aria-level={5}>
                <PostTitleWithIcons post={post} />
              </span>
            </div>
            <footer className={styles.contributors}>
              <AvatarStack
                link
                users={users}
                showHoverCard
                longHoverDelay
                noWrap
              />
            </footer>
            <footer className={styles.meta}>{meta}</footer>
            {renderOverflowMenu && (
              <aside
                className={classNames(styles.overflowMenu, {
                  [styles.isOpen]: isOverflowMenuOpen,
                })}
              >
                <OverflowMenu
                  preventCloseOnContentClick
                  button={
                    <Button
                      className={styles.overflowMenuButton}
                      aria-label="Actions"
                    >
                      <SVGIcon>{overflowMenuIcon}</SVGIcon>
                    </Button>
                  }
                  onOpenChange={setIsOverflowMenuOpen}
                  open={isOverflowMenuOpen}
                >
                  <div className={styles.overflowMenuContent}>
                    {renderOverflowMenu(() => setIsOverflowMenuOpen(false))}
                  </div>
                </OverflowMenu>
              </aside>
            )}
          </div>
        )}
      </HoverPreviewable>
    </NestableLink>
  );
};

export default PostRow;
